import { NgRedux } from '@redux/redux-compatibility.service';
import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { DataElement } from 'src/app/shared/models/common';
import { ConfigScope } from 'src/app/shared/models/integration-type.model';
import { Env } from 'src/app/shared/models/user.model';
import { actionsList, AppState } from 'src/app/shared/redux/store';
import { AuthService } from 'src/app/shared/services/auth.service';
import { JsonHandlingService } from '@shared/services/json-handling.service';
import { CommonService } from '@shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-mng-env',
    templateUrl: './mng-env.component.html',
    styleUrls: ['./mng-env.component.scss'],
    standalone: false
})
export class MngEnvComponent implements OnInit {
  loading: boolean;
  editEnvOpenSw: boolean;
  integrationConfigurationScope: ConfigScope = ConfigScope.Environment;
  curEnv: Env;
  displayConfigureIntegration: boolean;
  @ViewChildren('jsonDialog') 
  dialogs: QueryList<Dialog>;
  displayJsonDialog: boolean = false;
  jsonDialogData: any = {};
  curEnvId: number = 0;
  jsonReadOnlyData: any = {};
  envSpecificUiConfig: object = {};
  envEffectiveUiConfig: object = {};
  displayUiConfigDialog: boolean = false;
  curRow: any = null;
  statusList: any[] = [
    { label: 'Library', value: "DOC_IND" },
    { label: 'Precheck', value: "DOC_PRECHECK" }
  ];

  filterStatus: any;
  filterAssetType: any = null;
  filterLanguage: any = null;
  filterCatalogType: any = null;


  get envList(): Env[] {
    return this.ngRedux.getState().envList;
  }

  get assetTypeList(): DataElement[] {
    return this.ngRedux.getState().assetTypes;
  }

  get catalogTypeList(): DataElement[] {
    return this.ngRedux.getState().catTypeList;
  }

  get langList(): DataElement[] {
    return this.ngRedux.getState().catLangList;
  }

  constructor(
    private ngRedux: NgRedux<AppState>,
    private authService: AuthService,
    private jsonHandlingService: JsonHandlingService,
    private commonService: CommonService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {}

  refreshEnvList() {
    this.loading = true;
    this.authService.getEnvList().subscribe(
      (data) => {
        this.loading = false;
      },
      (err) => {
        this.loading = false;
      }
    );
  }

  addEnv() {
    this.curEnv = new Env();
    this.editEnvOpenSw = true;
  }

  editEnv(env: Env) {
    this.curEnv = env;
    this.editEnvOpenSw = true;
  }

  onConfigureIntegration(env: Env): void {
    this.curEnv = env;
    this.displayConfigureIntegration = true;
  }

  onIntegrationConfigured(event: Event): void {
    this.displayConfigureIntegration = false;
  }

  onAssetTypeChanged(event: any) {
    this.filterAssetType = event.value;
    if (this.curRow) {
      this.refreshJsonData(this.curRow);
    }
  }

  onCatalogTypeChanged(event: any) {
    this.filterCatalogType = event.value; 
    if (this.curRow) {
      this.refreshJsonData(this.curRow);
    }
  }

  onFieldValueChanged(event: any) {
    this.filterStatus = event.value;
    if (this.curRow) {
      this.refreshJsonData(this.curRow);
    }
  }

  onLanguageValueChanged(event: any) {
    this.filterLanguage = event.value;
    if (this.curRow) {
      this.refreshJsonData(this.curRow);
    }
  }

  onDialogHide() {
    this.resetFilters();
    this.curRow = null;
  }

  resetFilters() {
    this.filterStatus = null;
    this.filterAssetType = null;
    this.filterLanguage = null;
    this.filterCatalogType = null;
  }


  onConfigureProcessing(row: any): void {
    this.curRow = row;
    this.refreshJsonData(row);
  }

  refreshJsonData(row: any) {
    if (!row) return;

    const selectedAssetType = this.assetTypeList.find(a => a.id === this.filterAssetType);
    const selectedCatalogType = this.catalogTypeList.find(c => c.id === this.filterCatalogType);
    const assetTypeText = selectedAssetType?.text;
    const catalogTypeText = selectedCatalogType?.id;

    this.jsonHandlingService
      .getJsonDataReadOnly(this.filterStatus, assetTypeText, this.filterLanguage, catalogTypeText)
      .subscribe(
        (readOnlyData) => {
          this.jsonReadOnlyData = readOnlyData;

          this.jsonHandlingService.getJsonData(row.id, this.filterStatus, assetTypeText, this.filterLanguage, catalogTypeText).subscribe(
            (editableData) => {
              this.jsonDialogData = editableData;
              this.displayJsonDialog = true;
            },
            (error) => {
              console.error('Error fetching editable JSON data:', error);
            }
          );
        },
        (error) => {
          console.error('Error fetching read-only JSON data:', error);
        }
      );

    this.curEnvId = row.id;
  }
  
  
  handleJsonSave(event: any): void {
    console.log('Saving JSON data:', event);
    const selectedAssetType = this.assetTypeList.find(a => a.id === this.filterAssetType);
    const assetTypeText = selectedAssetType?.text;
    const selectedCatalogType = this.catalogTypeList.find(c => c.id === this.filterCatalogType);
    const catalogTypeText = selectedCatalogType?.id;

    this.jsonHandlingService.saveJsonData(this.curEnvId, event, this.filterStatus, assetTypeText, this.filterLanguage, catalogTypeText).subscribe(
      (response) => {
        console.log('Saved JSON:', response);  
        this.displayJsonDialog = false;
      },
      (error) => {
        console.error('Error saving JSON data:', error); 
      }
    );
  }

  handleJsonCancel(): void {    
    this.displayJsonDialog = false;
  }

  onConfigureUi(row: any): void {
    this.commonService.getUiConfig(row.id, false).subscribe(
      (uiConfig) => {
        this.envSpecificUiConfig = uiConfig;
        this.displayUiConfigDialog = true;
      },
      (error) => {
        console.error('Error fetching UI config:', error);
      }
    );

    this.commonService.getUiConfig(row.id, true).subscribe(
      (uiConfig) => {
        this.envEffectiveUiConfig = uiConfig;
        this.displayUiConfigDialog = true;
      },
      (error) => {
        console.error('Error fetching UI config:', error);
      }
    );
    this.curEnvId = row.id;
  }
  
  handleUiConfigSave(event: any): void {
    this.commonService.setUiConfig(this.curEnvId, event).subscribe(
      (uiConfig) => {
        this.ngRedux.dispatch({
            type: actionsList.UI_CONFIG_UPDATED,
            data: uiConfig,
          });
        this.displayUiConfigDialog = false;
      },
      (error) => {
        console.error('Error saving UI config data:', error); 
      }
    );
  }
}
